import { observer } from 'mobx-react-lite'
import { SearchResultItem } from '@/components/inputs/SearchResultItem'
import { FormRow } from '@/components/Form'
import {
  type WorkspaceSelection,
  type WorkspaceSelectorViewModel,
} from '@/features/inventory/admin/WorkspaceSelectorViewModel'
import {
  CustomSelect,
  SingleItemSelectedDefault,
} from '@/components/inputs/CustomSelect'
import { useCustomSelectStateFromInfiniteScrollProvider } from '@/components/inputs/infinite-scroll-provider-hook'
import { FormGroupContainer } from '@/components/FormContainer'
import { Avatar } from '@/components/Avatar'

export interface BundleFormWorkspaceDropdownContainerProps {
  vm: WorkspaceSelection
  workspaceSelectorVm: WorkspaceSelectorViewModel
  isEditingBundleView?: boolean
  label?: string
  testId?: string
}

export const BundleFormWorkspaceDropdownContainer = observer(
  function BundleFormWorkspaceDropdownContainer({
    vm,
    workspaceSelectorVm,
    isEditingBundleView = false,
    label,
    testId,
  }: BundleFormWorkspaceDropdownContainerProps) {
    const selectProps = useCustomSelectStateFromInfiniteScrollProvider({
      scrollProvider: workspaceSelectorVm.scrollProvider,
      searchText: workspaceSelectorVm.searchText,
      onSearchTextChange: workspaceSelectorVm.setSearchText,
      searchResults: workspaceSelectorVm.filteredWorkspaces,
    })

    return (
      <FormRow>
        <FormGroupContainer
          label={
            label || (isEditingBundleView ? 'Workspace' : 'Select workspace')
          }
          error={vm.workspaceValidation.getError('workspace')}
        >
          <CustomSelect
            {...selectProps}
            placeholder={isEditingBundleView ? '' : 'Select workspace'}
            testId={testId && `${testId}-dropdown`}
            itemToString={(v) => String(v?.name)}
            value={vm.workspace}
            multi={false}
            onChange={vm.setWorkspace}
            renderItem={(itemProps) => (
              <SingleItemSelectedDefault
                {...itemProps}
                visual={
                  <Avatar
                    size={24}
                    subject={{
                      displayName: itemProps.item.name,
                      avatar: itemProps.item.branding.logos.icon,
                    }}
                  />
                }
              />
            )}
            renderSearchItem={({
              item,
              itemToString: _itemToString,
              ...itemProps
            }) => (
              <SearchResultItem
                {...itemProps}
                data-test={testId && `${testId}-${item.name}`}
                visual={
                  <Avatar
                    size={24}
                    subject={{
                      avatar: item.branding.logos.icon,
                      displayName: item.name,
                    }}
                  />
                }
              >
                {item.name}
              </SearchResultItem>
            )}
            isDisabled={isEditingBundleView}
            icon={isEditingBundleView ? null : undefined}
          />
        </FormGroupContainer>
      </FormRow>
    )
  },
)
